import { useCallback, useEffect, useState } from "react";
import {
  API_DATE_TIME_FORMAT,
  contentHeight,
  DATE_TIME_FORMAT,
  EnumTypes,
  ENV_CONFIG,
} from "../../utils/constants";
import "./style.scss";
import Form from "../../components/Form";
import { getExpenseDetailTableConfig, getFormFields } from "./constants";
import {
  Box,
  Button,
  Container,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import Modal from "../../components/Modal/Modal";
import { ICreateExpense } from "../../types/ICreateExpense";
import { IExpense } from "../../types/IExpense";
import { Moment } from "moment";
import { ArrowCircleLeft, ArrowCircleRight } from "@mui/icons-material";
import { DateTimePicker } from "@mui/x-date-pickers";
import { getTodayTimings } from "../../utils/commonHelpers";
import Table from "../../components/Table/Table";
import { IExpenseHierarchy } from "../../types/IExpenseHierarchy";
import useStore from "../../store";
import { sortBy } from "lodash";
import SelectField from "../../components/SelectField";

const AdminExpense = () => {
  const theme = useTheme();
  const { startTime, endTime } = getTodayTimings();
  const [operationsStart, setOperationsStart] = useState<Moment>(startTime);
  const [operationsEnd, setOperationsEnd] = useState<Moment>(endTime);
  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  const [expenseTypeList, setExpenseTypeList] = useState<
    { id: string; title: string; base_type: { title: string } }[]
  >([]);
  const [expenseBrief, setExpenseBrief] = useState<IExpenseHierarchy | null>(
    null,
  );
  const { enums } = useStore();

  const expenseSourceOptions = [
    { label: "All sources", value: "all" },
    ...sortBy(enums[EnumTypes.EXPENSE_SOURCE] || [], ["label"]),
  ];

  const [expenseSource, setExpenseSource] = useState<string>(
    expenseSourceOptions?.[0]?.value,
  );

  const fetchAndSetExpenseTypeList = async () => {
    const response = await fetch(
      `${ENV_CONFIG.BACKEND_BASE_URL}/api/management/expense-type/`,
    );
    const data = await response.json();
    setExpenseTypeList(data.results);
  };

  const fetchAndSetExpenseBrief = useCallback(async () => {
    setExpenseBrief(null);

    let params: { [key: string]: string } = {
      from_date: operationsStart.format(API_DATE_TIME_FORMAT),
      to_date: operationsEnd.format(API_DATE_TIME_FORMAT),
    };

    if (expenseSource !== "all") {
      params["expense_source"] = expenseSource;
    }

    const url = new URL(
      "/api/superadmins/expenses-brief",
      ENV_CONFIG.BACKEND_BASE_URL,
    );

    url.search = new URLSearchParams(params).toString();

    const briefResponse = await fetch(url);
    const briefData = await briefResponse.json();
    setExpenseBrief(briefData);
  }, [operationsStart, operationsEnd, expenseSource]);

  useEffect(() => {
    fetchAndSetExpenseTypeList();
  }, []);

  useEffect(() => {
    fetchAndSetExpenseBrief();
  }, [fetchAndSetExpenseBrief]);

  const expenseTypeOptions = expenseTypeList.map((item) => ({
    label: item.title,
    value: item.id,
    group: item?.base_type.title,
  }));

  const formFirstFields = getFormFields(
    expenseTypeOptions,
    expenseSourceOptions.slice(1),
  );

  const handleConfirm = async (expense: ICreateExpense) => {
    const response = await fetch(
      `${ENV_CONFIG.BACKEND_BASE_URL}/api/management/expense/`,
      {
        method: "POST",
        body: JSON.stringify(expense),
        headers: { "content-type": "application/json" },
      },
    );

    if (response.status === 201) {
      setIsFormOpen(false);
      fetchAndSetExpenseBrief();
    }
  };

  const handleDeleteExpense = async (expense: IExpense) => {
    const value = prompt(
      `Are you sure you want to delete this order? Type "yes" to delete it.`,
    );

    if (value?.trim().toLowerCase() === "yes") {
      await fetch(
        `${ENV_CONFIG.BACKEND_BASE_URL}/api/management/expense/${expense.id}/`,
        { method: "DELETE" },
      );

      fetchAndSetExpenseBrief();
      alert(`Order#${expense.id.split("-")[4]}  deleted successfully`);
    }
  };

  return (
    <Container id="admin-expense-page-container">
      <Box className="header">
        <Box className="date-time-container">
          <ArrowCircleLeft
            className="icon"
            color="success"
            onClick={() => {
              const start = operationsStart.clone();
              const end = operationsEnd.clone();
              const diff = end.diff(operationsStart, "days") + 1;
              setOperationsStart(start.subtract(diff, "days"));
              setOperationsEnd(end.subtract(diff, "days"));
            }}
          />

          <Box display={"flex"} alignItems={"center"}>
            <DateTimePicker
              className="start-date"
              format={DATE_TIME_FORMAT}
              value={operationsStart}
              onChange={(val) => setOperationsStart(val || startTime)}
            />
          </Box>
          <Typography>-</Typography>
          <Box display={"flex"} alignItems={"center"}>
            <DateTimePicker
              className="end-date"
              format={DATE_TIME_FORMAT}
              value={operationsEnd}
              onChange={(val) => setOperationsEnd(val || endTime)}
            />
          </Box>

          <ArrowCircleRight
            className="icon"
            color="success"
            onClick={() => {
              const start = operationsStart.clone();
              const end = operationsEnd.clone();
              const diff = end.diff(operationsStart, "days") + 1;
              setOperationsStart(start.add(diff, "days"));
              setOperationsEnd(end.add(diff, "days"));
            }}
          />

          <Box display={"flex"} alignItems={"center"} ml={2}>
            <SelectField
              value={expenseSource}
              options={expenseSourceOptions}
              disabled={false}
              onChange={setExpenseSource}
            />
          </Box>
        </Box>

        <Button onClick={() => setIsFormOpen(true)} variant="contained">
          ADD EXPENSE
        </Button>
      </Box>

      <Box height={contentHeight}>
        {!!expenseBrief && (
          <Table
            height={contentHeight}
            {...getExpenseDetailTableConfig(expenseBrief, handleDeleteExpense)}
          />
        )}

        {!!expenseBrief && (
          <MuiTable sx={{ backgroundColor: theme.palette.action.hover }}>
            <TableBody>
              <TableRow>
                <TableCell align="right">
                  <Typography>
                    <strong>Total: </strong>
                    {expenseBrief.total_spending}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </MuiTable>
        )}
      </Box>

      <Modal open={isFormOpen} onClose={() => setIsFormOpen(false)}>
        <Form formFields={formFirstFields} onSubmit={handleConfirm} />
      </Modal>
    </Container>
  );
};

export default AdminExpense;
